import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { OTRStatusBadge, DailyExpenseDashboard } from '../components';
import { INITIAL_OTR_FORM_STATE, TIER_OPTIONS } from '../constants/otrFormState';
import { TRAVEL_PURPOSE, PURPOSE_REQUIRES_CUSTOMER, MODE_OF_TRAVEL, MODE_OF_TRAVEL_LABELS, OTR_STATUS, PURPOSE_I18N_KEYS, MODE_I18N_KEYS } from '../constants/otrEnums';
import { cn } from '../../../lib/utils';
import { useOTRValidation } from '../hooks/useOTRValidation';
import { createTravelRequest, updateTravelRequest, submitTravelRequest, getTravelRequestById, getExpensePoliciesByTier, getCustomerNames } from '../../../services/outstationTravelService';
import { OTRSelect } from '../components/OTRSelect';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { OTRDateField } from '../components/OTRDateField';
import { Textarea } from '../../../components/ui/textarea';
import { CompanyArrangedSelect } from '../components/CompanyArrangedSelect';
import { errMsg } from '../utils/errorMessage';
import { isRequestApproved, canAddExpenseLines, canEditTravelRequest, EXPENSE_TABS, EXPENSE_LOCK_MESSAGE } from '../utils/otrCalculations';
import sessionDB from '../../../utils/sessionDB';
import {
  getCompanyArrangeableHeads,
  getEligibleExpenseHeads,
  buildArrangedPayload,
  normalizeArrangedMap,
} from '../utils/companyArranged';
import { formatOtrDate } from '../utils/formatOtrDate';

const OTR_TABS = [
  { key: 'travelInfo', labelKey: 'outstation.travelInfo' },
  { key: 'dailyExpense', labelKey: 'outstation.dailyExpenseEntry' },
];

const calcTripDays = (start, end) => {
  if (!start || !end) return null;
  const days = Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
  return days > 0 ? days : null;
};



export const CreateEditOTRPage = () => {
  const { otrId, id } = useParams();
  const requestId = otrId || id;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = !!requestId;

  const [formData, setFormData] = useState({
    ...INITIAL_OTR_FORM_STATE,
    tenantId: sessionDB.getTenantId(),
    empId: sessionDB.getEmpId()
  });
  const [policyHeads, setPolicyHeads] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('travelInfo');
  const { errors, validateOTRForm } = useOTRValidation();

  // Expense Report / Daily Expense Entry exist only for an approved request.
  const expensesUnlocked = isEdit && isRequestApproved(formData.status, formData.sentBackFrom);

  useEffect(() => {
    if (!expensesUnlocked && EXPENSE_TABS.includes(activeTab)) setActiveTab('travelInfo');
  }, [expensesUnlocked, activeTab]);

  const requiresCustomer = PURPOSE_REQUIRES_CUSTOMER.includes(formData.purpose);
  const tripDays = calcTripDays(formData.dateOfTravel, formData.dateOfReturn);

  useEffect(() => {
    if (isEdit) {
      getTravelRequestById(requestId).then(data => {
        // Only the owner can edit, and only a Draft or a request sent back
        // from the request stage. Rejected / other states are not editable.
        const isOwner = Number(data.empId) === Number(sessionDB.getEmpId());
        if (!isOwner || !canEditTravelRequest(data.status, data.sentBackFrom)) {
          navigate(`/outstation-travel/${requestId}`, { replace: true });
          return;
        }
        setFormData({
          ...data,
          // Form fields the user edits; the API names differ.
          customer: data.purposeRecordName || '',
          advanceRequested: data.advanceRequest || '',
          companyArrangedExpenseHeads: normalizeArrangedMap(data.companyArranged),
        });
        setLoading(false);
      }).catch((err) => {
        console.error('Failed to load travel request', err);
        setSaveError(errMsg(err, 'Could not load this travel request.'));
        setLoading(false);
      });
    }
  }, [requestId, isEdit, navigate]);

  // When tier is assigned (usually by manager, but for form we might need it)
  useEffect(() => {
    if (!formData.tierId) {
      setPolicyHeads([]);
      return undefined;
    }
    let cancelled = false;
    setPolicyHeads([]);
    setPolicyError('');
    setPolicyLoading(true);
    getExpensePoliciesByTier(formData.tenantId, formData.tierId)
      .then((heads) => { if (!cancelled) setPolicyHeads(Array.isArray(heads) ? heads : []); })
      .catch((err) => {
        console.error('Failed to load expense policy', err);
        if (!cancelled) setPolicyError('Could not load the expense policy for this tier.');
      })
      .finally(() => { if (!cancelled) setPolicyLoading(false); });
    return () => { cancelled = true; };
  }, [formData.tierId, formData.tenantId]);

  // Only heads the policy allows to be company arranged are offered as a choice;
  // employee expenses are limited to everything that is not company arranged.
  const arrangeableHeads = getCompanyArrangeableHeads(policyHeads);
  const eligibleExpenseHeads = getEligibleExpenseHeads(policyHeads, formData.companyArrangedExpenseHeads);

  // Customers for the dropdown: GET /customer-info/search/status?accountStatus=Active
  const [customerOptions, setCustomerOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getCustomerNames()
      .then((names) => { if (!cancelled) setCustomerOptions(names); })
      .catch((err) => console.error('Failed to load customers', err));
    return () => { cancelled = true; };
  }, []);

  // Stored as one string ("A, B") in purposeRecordName.
  const selectedCustomers = (formData.customer || '')
    .split(',').map((s) => s.trim()).filter(Boolean);
  const toggleCustomer = (name) => {
    const next = selectedCustomers.includes(name)
      ? selectedCustomers.filter((n) => n !== name)
      : [...selectedCustomers, name];
    handleChange('customer', next.join(', '));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Switching to a purpose that doesn't need a Customer clears any
  // previously entered value so a stale customer doesn't get submitted.
  const handlePurposeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      purpose: value,
      customer: PURPOSE_REQUIRES_CUSTOMER.includes(value) ? prev.customer : '',
    }));
  };

  const handleTierChange = (tierId) => {
    setFormData(prev => ({
      ...prev,
      tierId,
      companyArrangedExpenseHeads: {},
    }));
  };

  const handleSelectionChange = (headId, isSelected) => {
    setFormData(prev => ({
      ...prev,
      companyArrangedExpenseHeads: {
        ...prev.companyArrangedExpenseHeads,
        [headId]: isSelected
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateOTRForm(formData)) return;

    setSaveError('');
    setSubmitting(true);
    try {
      // Send only policy-allowed heads with the employee's yes/no choice.
      const payloadForm = {
        ...formData,
        // Never resend the old status/reasons: that would re-trigger a send-back
        // or fail validation. Resubmission is done by the submit call below.
        status: undefined,
        rejectReason: undefined,
        sentBackReason: undefined,
        cancelReason: undefined,
        // Use the values edited in the form.
        purposeRecordName: formData.customer,
        advanceRequest: Number(formData.advanceRequested) || 0,
        companyArranged: buildArrangedPayload(policyHeads, formData.companyArrangedExpenseHeads),
      };
      if (isEdit) {
        await updateTravelRequest(requestId, payloadForm);

        // The UI has one Submit Request action. If a previously created request
        // is Draft/Sent_Back, update its fields first and then re-enter approval.
        if (
          formData.status === OTR_STATUS.DRAFT ||
          formData.status === OTR_STATUS.SENT_BACK
        ) {
          await submitTravelRequest(requestId);
        }
      } else {
        // POST /add creates the request as a Draft. It only reaches the
        // approver's report after it is submitted for approval.
        const created = await createTravelRequest(payloadForm);
        const newId = created?.travelRequestId;
        if (newId && (!created.status || created.status === OTR_STATUS.DRAFT)) {
          await submitTravelRequest(newId);
        }
      }
      navigate('/outstation-travel');
    } catch (err) {
      console.error('Save failed', err);
      setSaveError(errMsg(err, 'Could not save the travel request.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <AppLayout><div className="py-20 text-center">Loading...</div></AppLayout>;

  return (
    <AppLayout>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Header bar */}
        <div className="p-4 sm:p-6 bg-white rounded-2xl border border-[var(--color-border)] shadow-sm flex items-start justify-between gap-4">
          <div>
            <PageBackLink to="/outstation-travel" label={t('outstation.myTravelRequests')} />
            <div className="flex items-center gap-3 mt-2 mb-1">
              <h1 className="text-lg font-bold text-foreground leading-tight">
                {formData.requestRef || (formData.travelRequestId ? `OTR-${formData.travelRequestId}` : 'New Travel Request')}
              </h1>
              <OTRStatusBadge status={formData.status || OTR_STATUS.DRAFT} />
            </div>
            <p className="text-sm text-[var(--color-text-secondary)]">
              {formData.placeOfTravel || `${t('outstation.placeOfTravel')} —`}
              {' · '}{formatOtrDate(formData.dateOfTravel)} {t('outstation.dateRangeTo')} {formatOtrDate(formData.dateOfReturn)}
              {' · '}{formData.employeeName || 'Me'}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">{t('outstation.tripDays')}</div>
            <div className="text-lg font-bold text-foreground">{tripDays ?? '—'}</div>
          </div>
        </div>

        {formData.status === OTR_STATUS.SENT_BACK && (
          <div className="p-4 rounded-2xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 text-sm">
            <span className="font-bold">Sent back for changes.</span>{' '}
            {formData.sentBackReason || 'Please review, update and resubmit.'}
          </div>
        )}

        {saveError && (
          <div className="p-4 rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 text-sm text-[var(--color-error)] font-medium">
            {saveError}
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap border-b border-[var(--color-text-border)]/65 bg-white rounded-t-2xl px-2">
          {OTR_TABS.map(tab => {
          const locked = EXPENSE_TABS.includes(tab.key) && !expensesUnlocked;
          return (
            <button
              key={tab.key}
              type="button"
              disabled={locked}
              title={locked ? EXPENSE_LOCK_MESSAGE : undefined}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "py-3 px-5 text-sm font-bold border-b-2 transition -mb-[2px]",
                activeTab === tab.key
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-transparent text-[var(--color-text-secondary)] hover:text-foreground",
                locked && "opacity-50 cursor-not-allowed hover:text-[var(--color-text-secondary)]"
              )}
            >
              {locked ? '🔒 ' : ''}{t(tab.labelKey)}
            </button>
          );
        })}
        </div>

        {activeTab === 'travelInfo' && (
          <div className="p-4 sm:p-8 bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm">
            <h2 className="text-sm font-bold text-foreground mb-6">{t('outstation.tripDetails')}</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.dateOfTravel')}<span className="text-[var(--color-error)]"> *</span></label>
                <OTRDateField
                  value={formData.dateOfTravel}
                  onChange={(v) => handleChange('dateOfTravel', v)}
                  hasError={!!errors.dateOfTravel}
                />
                {errors.dateOfTravel && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.dateOfTravel}</span>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.dateOfReturn')}<span className="text-[var(--color-error)]"> *</span></label>
                <OTRDateField
                  value={formData.dateOfReturn}
                  onChange={(v) => handleChange('dateOfReturn', v)}
                  min={formData.dateOfTravel}
                  hasError={!!errors.dateOfReturn}
                />
                {errors.dateOfReturn && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.dateOfReturn}</span>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.placeOfTravel')}<span className="text-[var(--color-error)]"> *</span></label>
                <input
                  type="text"
                  value={formData.placeOfTravel}
                  onChange={(e) => handleChange('placeOfTravel', e.target.value)}
                  placeholder={t('outstation.placeOfTravelPlaceholder')}
                  className={`h-11 px-4 text-sm rounded-xl border-[var(--color-text-border)] bg-white outline-none transition-all ${errors.placeOfTravel ? 'border-[var(--color-error)]' : 'focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10'
                    }`}
                />
                {errors.placeOfTravel && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.placeOfTravel}</span>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.purpose')}<span className="text-[var(--color-error)]"> *</span></label>
                <OTRSelect
                  value={formData.purpose}
                  onChange={handlePurposeChange}
                  options={Object.values(TRAVEL_PURPOSE).map(p => ({ value: p, label: t(`outstation.purposeOptions.${PURPOSE_I18N_KEYS[p]}`) }))}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.modeOfTravel')}<span className="text-[var(--color-error)]"> *</span></label>
                <OTRSelect
                  value={formData.modeOfTravelId}
                  onChange={(v) => handleChange('modeOfTravelId', v ? Number(v) : null)}
                  options={Object.entries(MODE_OF_TRAVEL_LABELS).map(([id]) => ({ value: Number(id), label: t(`outstation.modeOptions.${MODE_I18N_KEYS[id]}`) }))}
                  hasError={!!errors.modeOfTravelId}
                />
                <span className="text-[10px] text-[var(--color-text-secondary)] ml-1">{t('outstation.fuelHint')}</span>
              </div>

              {/* Customer — only shown when the selected Purpose requires it */}
              {requiresCustomer && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.customers')}<span className="text-[var(--color-error)]"> *</span></label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`flex h-11 w-full items-center justify-between rounded-xl border bg-white px-3 text-sm ${errors.customer ? 'border-[var(--color-error)]' : 'border-[var(--color-text-border)]'} ${selectedCustomers.length === 0 ? 'text-muted-foreground' : ''}`}
                      >
                        <span className="truncate">
                          {selectedCustomers.length ? selectedCustomers.join(', ') : t('outstation.selectCustomers')}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[260px] max-h-64 overflow-y-auto p-1">
                      {customerOptions.map((name) => (
                        <label key={name} className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent/10">
                          <input
                            type="checkbox"
                            checked={selectedCustomers.includes(name)}
                            onChange={() => toggleCustomer(name)}
                            className="h-4 w-4 accent-[var(--color-primary)]"
                          />
                          <span className="flex-1">{name}</span>
                        </label>
                      ))}
                    </PopoverContent>
                  </Popover>
                  {errors.customer && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.customer}</span>}
                  {selectedCustomers.length > 0 && (
                    <div className="mt-1 rounded-xl border border-[var(--color-text-border)] bg-[var(--color-bg-secondary,#f8fafc)] p-3">
                      <div className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-2">
                        {t('outstation.selectedCustomers', { count: selectedCustomers.length })}
                      </div>
                      <ul className="flex flex-col gap-1.5">
                        {selectedCustomers.map((name) => (
                          <li key={name} className="flex items-center justify-between gap-2 rounded-lg bg-white border border-[var(--color-text-border)] px-3 py-1.5 text-sm font-medium text-foreground">
                            <span className="truncate">{name}</span>
                            <button
                              type="button"
                              onClick={() => toggleCustomer(name)}
                              aria-label={t('outstation.removeCustomer', { name })}
                              className="shrink-0 text-base leading-none text-[var(--color-text-secondary)] hover:text-[var(--color-error)]"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.tier')}<span className="text-[var(--color-error)]"> *</span></label>
                <OTRSelect
                  value={formData.tierId}
                  onChange={(v) => handleTierChange(v ? Number(v) : null)}
                  options={TIER_OPTIONS.map((o) => ({ ...o, label: t('outstation.tierN', { n: o.value }) }))}
                />
                <span className="text-[10px] text-[var(--color-text-secondary)] ml-1">{t('outstation.tierHint')}</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.companyArrangedExpenses')}</label>
                <CompanyArrangedSelect
                  heads={arrangeableHeads}
                  selections={formData.companyArrangedExpenseHeads || {}}
                  onChange={handleSelectionChange}
                  loading={policyLoading}
                  error={policyError}
                  disabled={!formData.tierId}
                />
                <span className="text-[10px] text-[var(--color-text-secondary)] ml-1">{t('outstation.companyArrangedHint')}</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.advanceRequest')}</label>
                <input
                  type="number"
                  min="0"
                  value={formData.advanceRequested}
                  onChange={(e) => handleChange('advanceRequested', e.target.value)}
                  placeholder={t('outstation.enterAdvanceAmount')}
                  className="h-11 px-4 text-sm rounded-xl border-[var(--color-text-border)] bg-white outline-none transition-all focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10"
                />
                <span className="text-[10px] text-[var(--color-text-secondary)] ml-1">{t('outstation.advanceHint')}</span>
              </div>

              <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-2">
                <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">{t('outstation.remarks')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('outstation.optional')}
                  className="min-h-[44px] rounded-xl border-[var(--color-text-border)] bg-white text-sm resize-none"
                />
              </div>
            </div>

          </div>
        )}

        {activeTab === 'dailyExpense' && expensesUnlocked && (
          <div className="p-4 sm:p-8 bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm">
            {isEdit ? (
              <DailyExpenseDashboard
                requestId={requestId}
                canAddExpenses={canAddExpenseLines(formData.status, formData.sentBackFrom)}
                onClaimSubmitted={() =>
                  getTravelRequestById(requestId).then((d) =>
                    setFormData((prev) => ({ ...prev, status: d.status }))
                  )
                }
                policyHeads={eligibleExpenseHeads}
                tripDays={tripDays}
                travelStartDate={formData.dateOfTravel}
                advanceRequested={formData.advanceRequest ?? formData.advanceRequested}
                currencySymbol={formData.currencySymbol || '₹'}
              />
            ) : (
              <>
                <h2 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wider">{t('outstation.dailyExpenseEntry')}</h2>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Daily expense entries can be logged once the request is submitted and approved.
                </p>
              </>
            )}
          </div>
        )}

        {/* Footer / submission bar */}
        <div className="p-4 sm:p-6 bg-white rounded-2xl border border-[var(--color-border)] shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Submitting routes this to {formData.approverName || 'your manager'}, then the Finance Team.
          </p>
          <div className="flex gap-3 [&>*]:w-full sm:[&>*]:w-auto">
            <Button
              type="submit"
              disabled={submitting}
              className="h-11 px-8 rounded-xl bg-[var(--color-primary)] text-white font-bold shadow-lg hover:bg-[var(--color-accent)] transition-all"
            >
              {submitting ? 'Saving...' : (isEdit ? 'Update Request' : 'Submit Request')}
            </Button>
          </div>
        </div>
      </form>
    </AppLayout>
  );
};